// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { t } from "@/lib/i18n";
import type { VenueStatus } from "@/lib/venue-helpers";
import type { Locale } from "@/lib/types";

interface StatusBadgeProps {
  status: VenueStatus;
  locale: Locale;
  size?: "sm" | "md";
}

export default function StatusBadge({ status, locale, size = "sm" }: StatusBadgeProps) {
  const sizeClasses = size === "md"
    ? "px-3 py-1.5 text-sm gap-2"
    : "px-2.5 py-1 text-xs gap-1.5";

  switch (status.state) {
    case "open":
      return (
        <span className={`shrink-0 inline-flex items-center rounded-full font-semibold status-open ${sizeClasses}`}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          {t(locale, "open_until").replace("{time}", status.closesAt)}
        </span>
      );

    case "closing_soon":
      return (
        <span className={`shrink-0 inline-flex items-center rounded-full font-semibold status-closing ${sizeClasses}`}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
          </span>
          {t(locale, "closing_soon").replace("{min}", String(status.minutesLeft))}
        </span>
      );

    case "opens_today":
      return (
        <span className={`shrink-0 inline-flex items-center rounded-full font-medium text-violet-300 bg-violet-500/10 border border-violet-500/20 ${sizeClasses}`}>
          <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t(locale, "opens_at").replace("{time}", status.opensAt)}
        </span>
      );

    case "opens_tomorrow":
      return (
        <span className={`shrink-0 inline-flex items-center rounded-full font-medium status-closed ${sizeClasses}`}>
          {t(locale, "opens_tomorrow").replace("{time}", status.opensAt)}
        </span>
      );

    case "closed_today":
      return (
        <span className={`shrink-0 inline-flex items-center rounded-full font-medium status-closed ${sizeClasses}`}>
          {t(locale, "closed_today")}
        </span>
      );
  }
}

/** Compact confidence indicator — shows data quality as dots */
export function ConfidenceDots({ confidence }: { confidence: number | null }) {
  if (confidence === null || confidence === undefined) return null;

  const level = confidence >= 0.8 ? 3 : confidence >= 0.5 ? 2 : 1;
  const color = level === 3 ? "bg-emerald-400" : level === 2 ? "bg-amber-400" : "bg-zinc-500";
  const label = level === 3 ? "Verified" : level === 2 ? "Likely" : "Unverified";

  return (
    <span className="inline-flex items-center gap-1" title={`Schedule confidence: ${Math.round(confidence * 100)}% — ${label}`}>
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`w-1 h-1 rounded-full ${i <= level ? color : "bg-zinc-700"}`}
        />
      ))}
    </span>
  );
}

/** Schedule note badge — shows irregular patterns, seasonal info */
export function ScheduleNote({ note, className = "" }: { note: string | null; className?: string }) {
  if (!note) return null;

  return (
    <span className={`inline-flex items-center gap-1 text-[11px] text-zinc-400 ${className}`}>
      <svg className="w-3 h-3 text-zinc-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="truncate">{note}</span>
    </span>
  );
}

/** Source badges — shows where schedule data came from */
export function SourceBadges({ sources }: { sources: string[] | null }) {
  if (!sources || sources.length === 0) return null;

  const sourceLabels: Record<string, { icon: string; label: string }> = {
    "facebook_events": { icon: "📅", label: "Facebook Events" },
    "llm": { icon: "🤖", label: "AI verified" },
    "website": { icon: "🌐", label: "Website" },
    "google_reviews": { icon: "⭐", label: "Google Reviews" },
    "ai_vision": { icon: "👁", label: "AI Vision" },
    "ai_crossref": { icon: "🔗", label: "Cross-referenced" },
    "facebook_page": { icon: "📘", label: "Facebook" },
    "instagram": { icon: "📸", label: "Instagram" },
    "tiktok": { icon: "🎵", label: "TikTok" },
    "google_places": { icon: "📍", label: "Google Maps" },
  };

  return (
    <div className="flex flex-wrap gap-1">
      {sources.slice(0, 4).map((src) => {
        const info = sourceLabels[src] || { icon: "📋", label: src };
        return (
          <span
            key={src}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/[0.04] text-zinc-400 border border-white/[0.06]"
            title={info.label}
          >
            <span className="text-[9px]">{info.icon}</span>
            {info.label}
          </span>
        );
      })}
      {sources.length > 4 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-zinc-500">
          +{sources.length - 4}
        </span>
      )}
    </div>
  );
}
